import { useCallback, useState } from "react"
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons"

import { RefetchIntervals } from "../../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    MeetingAudienceFilterType,
    MeetingOrderFilterType,
} from "../../../../../shared/components/filters/orderTypeFilter/helpers"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { EmptyContent, EmptyContentVariant } from "../../../../../shared/components/skeleton/EmptyContent"
import type { DtDateRange } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { usePrintPageOnly } from "../../../../../shared/hooks/usePrintPageOnly"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { groupMeetingsByDay } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/meetingDayGroups"
import { MeetingsDayList } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingsDayList"
import { UpcomingOrdersPanel } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/UpcomingOrdersPanel"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { ResourceType } from "../../../admin/organization/resources/resourcesTypes"
import { useCurrentServiceCenter } from "../../../store/serviceCenterHooks"
import { ServiceCenterCalendar } from "../../calendar/pages/ServiceCenterCalendar"
import { loadMeetingAudienceFilter, saveMeetingAudienceFilter } from "../../home/components/meetigs/helpers"

import { useExportUpcomingMeetings } from "../hooks/useExportUpcomingMeetings"
import { fetchServiceCenterUpcomingMeetings, ServiceCenterUpcomingMeetingsQuery } from "../upcomingMeetingsApi"
import { countGroupKinds, toUpcomingMeetingsQueryParams, toUpcomingMeetingsView } from "../upcomingMeetingsLib"
import { ALL_RESOURCE_TYPES, toUpcomingMeetingRow, UpcomingMeetingsPeriod } from "../upcomingMeetingsTypes"
import type { ResourceTypeFilter, UpcomingMeetingsResponse } from "../upcomingMeetingsTypes"
import { UpcomingMeetingTags } from "./UpcomingMeetingTags"
import { UpcomingMeetingsGroupCount } from "./UpcomingMeetingsGroupCount"
import { UpcomingMeetingsToolbar } from "./UpcomingMeetingsToolbar"

const EMPTY_RESOURCE_TYPE_COUNTS: Readonly<Record<ResourceTypeFilter, number>> = {
    [ALL_RESOURCE_TYPES]: 0,
    [ResourceType.ROOM]: 0,
    [ResourceType.MOBILE_AV]: 0,
    [ResourceType.CAR]: 0,
}

export function UpcomingMeetings() {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { serviceCenterId } = useCurrentServiceCenter()
    const printPageOnly = usePrintPageOnly()

    const [period, setPeriod] = useState<UpcomingMeetingsPeriod>(UpcomingMeetingsPeriod.TODAY)
    const [customRange, setCustomRange] = useState<DtDateRange>(() => {
        const today = dateTimeFactory.now().startOf("day")
        return { startDate: today, endDate: today }
    })
    const [orderType, setOrderType] = useState<MeetingOrderFilterType>(MeetingOrderFilterType.ALL)
    const [resourceType, setResourceType] = useState<ResourceTypeFilter>(ALL_RESOURCE_TYPES)
    const [audience, setAudience] = useState<MeetingAudienceFilterType>(loadMeetingAudienceFilter)
    const [search, setSearch] = useState("")
    const [isCalendarActive, setIsCalendarActive] = useState(false)

    const selectView = useCallback(
        (data: UpcomingMeetingsResponse) => {
            const rows = (data?.orders ?? []).map(toUpcomingMeetingRow)
            const view = toUpcomingMeetingsView(rows, { orderType, resourceType, audience, search })
            return { ...view, groups: groupMeetingsByDay(view.rows, dateTimeFactory, l10n, { withShortDate: true }) }
        },
        [orderType, resourceType, audience, search, dateTimeFactory, l10n],
    )

    const meetingsResult = useApiQuery(fetchServiceCenterUpcomingMeetings, {
        queryName: ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            ...toUpcomingMeetingsQueryParams(period, customRange, dateTimeFactory.now()),
        },
        enabled: Boolean(serviceCenterId),
        select: selectView,
        refetchInterval: RefetchIntervals.short,
    })

    const rows = meetingsResult.data?.rows ?? []
    const groups = meetingsResult.data?.groups ?? []
    const handleExport = useExportUpcomingMeetings(rows)

    const handleCustomRangeApply = (range: DtDateRange) => {
        setCustomRange(range)
        setPeriod(UpcomingMeetingsPeriod.CUSTOM)
    }

    const handleOrderTypeChange = (nextOrderType: MeetingOrderFilterType) => {
        setIsCalendarActive(false)
        setOrderType(nextOrderType)
        setResourceType(ALL_RESOURCE_TYPES)
    }

    // the search field is hidden in calendar mode, so a kept query would filter the list invisibly on return
    const handleShowCalendar = () => {
        setIsCalendarActive(true)
        setSearch("")
    }

    const handleAudienceChange = (nextAudience: MeetingAudienceFilterType) => {
        setAudience(nextAudience)
        saveMeetingAudienceFilter(nextAudience)
    }

    return (
        <>
            <UpcomingMeetingsToolbar
                isCalendarActive={isCalendarActive}
                isLoading={meetingsResult.isInitialLoading}
                period={period}
                customRange={customRange}
                orderType={orderType}
                resourceType={resourceType}
                resourceTypeCounts={meetingsResult.data?.resourceTypeCounts ?? EMPTY_RESOURCE_TYPE_COUNTS}
                audience={audience}
                search={search}
                onPresetSelect={setPeriod}
                onCustomRangeApply={handleCustomRangeApply}
                onOrderTypeChange={handleOrderTypeChange}
                onResourceTypeChange={setResourceType}
                onAudienceChange={handleAudienceChange}
                onSearchChange={setSearch}
                onPrint={printPageOnly}
                onExport={handleExport}
                onShowCalendar={handleShowCalendar}
            />

            {isCalendarActive ? (
                <ServiceCenterCalendar />
            ) : (
                <LocalizedStrict
                    id="user-home-meetings-panel-actions"
                    attrs={{ collapseAriaLabel: true, expandAriaLabel: true }}
                >
                    <UpcomingOrdersPanel
                        icon={faCalendarDays}
                        iconBackgroundColor={tailwindPalette.blue[100]}
                        iconColor={tailwindPalette.blue[400]}
                        titleId="user-home-upcoming-meetings"
                        titleFallback="Meetings"
                        counter={rows.length}
                        collapseAriaLabel=""
                        expandAriaLabel=""
                    >
                        {meetingsResult.isInitialLoading ? (
                            <LoadingSpinner />
                        ) : !groups.length ? (
                            <LocalizedStrict
                                id="service-center-reports-upcoming-meetings-empty"
                                attrs={{ title: true }}
                            >
                                <EmptyContent
                                    variant={EmptyContentVariant.SINGLE_LEFT}
                                    title="Nothing scheduled in this period"
                                />
                            </LocalizedStrict>
                        ) : (
                            <MeetingsDayList
                                groups={groups}
                                meetings={rows}
                                renderGroupMeta={(group) => (
                                    <UpcomingMeetingsGroupCount counts={countGroupKinds(group.meetings)} />
                                )}
                                renderTitleTags={(meeting) => <UpcomingMeetingTags meeting={meeting} />}
                            />
                        )}
                    </UpcomingOrdersPanel>
                </LocalizedStrict>
            )}
        </>
    )
}
